import React from "react";
import type { FlightItem, Location } from "@/types";
import { Plane } from "lucide-react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { DateTime } from "luxon";

dayjs.extend(timezone);
dayjs.extend(utc);

interface FlightItemsProps {
  flight: FlightItem;
  locationTimeZone: string;
  handleTransportButtonClick: (location: Location) => void;
  selectedLocation: Location;
  nextLocation: Location;
  showViewButton?: boolean;
}

const FlightItem: React.FC<FlightItemsProps> = ({
  flight,
  locationTimeZone,
  handleTransportButtonClick,
  selectedLocation,
  nextLocation,
  showViewButton = true,
}) => {
  if (!flight) return null;
  const uniqueAirlines = [
    ...new Set(flight.airline.map((airline) => airline.code)),
  ];

  const isArrival = flight.isArriving;

  const handleFlightClick = () => {
    handleTransportButtonClick(isArrival ? selectedLocation! : nextLocation!);
  };

  return (
    <div className="bg-neutral-100/70 rounded-lg py-3 px-4 w-full">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-center space-x-4">
          <Plane
            className={`w-5 h-5 text-neutral-600 ${
              isArrival ? "transform rotate-45" : "transform -rotate-45"
            }`}
          />
          <div className="flex flex-col items-start">
            <p className="text-sm text-neutral-700 font-medium mb-0.5">
              <span className="text-neutral-700">
                {flight.departureLocation.name}
              </span>
              {flight.departureLocation.code && (
                <span className="text-sm text-neutral-400 font-normal ml-1.5">
                  {flight.departureLocation.code}
                </span>
              )}
              <span className="text-neutral-700 mx-2">→</span>
              <span className="text-neutral-700">
                {flight.arrivalLocation.name}
              </span>
              {flight.arrivalLocation.code && (
                <span className="text-sm text-neutral-400 font-normal ml-1.5">
                  {flight.arrivalLocation.code}
                </span>
              )}
            </p>
            <p className="text-xs text-neutral-500 font-light">
              {isArrival
                ? `Arriving at ${DateTime.fromISO(flight.arrivalTime || "")
                    .setZone(locationTimeZone)
                    .toFormat("h:mm a")}`
                : `Departing at ${DateTime.fromISO(flight.departureTime || "")
                    .setZone(locationTimeZone)
                    .toFormat("h:mm a")}`}
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center space-x-2">
          {showViewButton && (
            <button
              onClick={handleFlightClick}
              className="space-x-2 flex items-center bg-white rounded-md ring-1 ring-neutral-300 py-2 px-3"
            >
              <h2 className="text-xs text-neutral-800">View</h2>
            </button>
          )}
          <div className="flex items-center space-x-2">
            {uniqueAirlines.map((airline, index) => (
              <img
                key={index}
                src={`https://images.kiwi.com/airlines/64x64/${airline}.png`}
                alt={airline}
                className="w-8 h-8 rounded-md"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightItem;
